import React, { useEffect, useState } from 'react';
import { Empty, Modal, Space, Spin, Typography, message } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { ingestionApi } from '../ingestionApi';

const { Text } = Typography;

const THUMB = 96;

/**
 * Picks product images from the item's gallery (the variant's own first, then the
 * other variants'): several to attach to a builder message, or one (`single`) to
 * become the texture. Returns the chosen urls through `onPick`; `taken` are the
 * urls already attached.
 */
export function GalleryPickerModal({ itemId, variantId, open, taken, max = 1, single = false, title = 'Attach from the gallery', okLabel = 'Attach', onPick, onClose }) {
    const [own, setOwn] = useState(null);
    const [others, setOthers] = useState([]);
    const [chosen, setChosen] = useState([]);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setChosen([]);
        setOwn(null);
        Promise.all([ingestionApi.getVariantImages(itemId, variantId), ingestionApi.getItemImages(itemId)])
            .then(([variant, item]) => {
                if (cancelled) return;
                const mine = variant.candidates || [];
                setOwn(mine);
                setOthers((item.images || []).filter((url) => !mine.includes(url)));
            })
            .catch((e) => {
                if (cancelled) return;
                message.error(e.message || 'Failed to load the gallery');
                setOwn([]);
            });
        return () => {
            cancelled = true;
        };
    }, [open, itemId, variantId]);

    const room = Math.max(0, max - (taken?.length || 0));
    const toggle = (url) => {
        if (taken?.includes(url)) return;
        setChosen((prev) => {
            if (single) return prev.includes(url) ? [] : [url];
            if (prev.includes(url)) return prev.filter((u) => u !== url);
            if (prev.length >= room) {
                message.info(`Up to ${max} images per message`);
                return prev;
            }
            return [...prev, url];
        });
    };

    const tile = (url) => {
        const attached = taken?.includes(url);
        const on = attached || chosen.includes(url);
        return (
            <div
                key={url}
                role="button"
                onClick={() => toggle(url)}
                style={{
                    position: 'relative',
                    width: THUMB,
                    height: THUMB,
                    borderRadius: 8,
                    overflow: 'hidden',
                    cursor: attached ? 'default' : 'pointer',
                    outline: on ? '2px solid #1677ff' : '1px solid rgba(128,128,128,0.35)',
                    opacity: attached ? 0.5 : 1,
                }}
            >
                <img src={url} alt="product" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                {on && <CheckCircleFilled style={{ position: 'absolute', top: 6, right: 6, color: '#1677ff', fontSize: 18, background: '#fff', borderRadius: 9 }} />}
            </div>
        );
    };

    const section = (label, urls) =>
        urls.length > 0 && (
            <div>
                {label && <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>{label}</Text>}
                <Space wrap size={8}>{urls.map(tile)}</Space>
            </div>
        );

    return (
        <Modal
            open={open}
            title={title}
            okText={chosen.length > 1 ? `${okLabel} ${chosen.length}` : okLabel}
            okButtonProps={{ disabled: chosen.length === 0 }}
            onOk={() => {
                onPick(chosen);
                onClose();
            }}
            onCancel={onClose}
            width={720}
            destroyOnHidden
        >
            {own === null ? (
                <Spin />
            ) : own.length === 0 && others.length === 0 ? (
                <Empty description="No product images" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {section(others.length > 0 ? 'This variant' : null, own)}
                    {section('Other variants', others)}
                </Space>
            )}
        </Modal>
    );
}
